import React from "react";
import { useTranslation } from "react-i18next";
import { CreateCommunityForm } from "../../../../types/forms";
import { StepFlowStep } from "../../../components/misc/StepFlow";
import FeaturesForm from "../../../components/misc/FeaturesForm";
import { getMinimumSubscriptionForFeatures } from "../../../../utils/FeatureUtils";
import AreYouSureModal from "../../../components/modals/AreYouSureModal";

type CommunityFeaturesViewProps = {
  setStep: (step: 1 | -1) => void;
  community: CreateCommunityForm;
  setCommunity: (community: CreateCommunityForm) => void;
}

export default function CommunityFeaturesView({ community, setCommunity, setStep }: CommunityFeaturesViewProps) {
  const { t } = useTranslation();
  const [warningOpen, setWarningOpen] = React.useState(false);
  const [deferredStepChange, setDeferredStepChange] = React.useState<null | 1 | -1>(null);

  const wrappedSetStep = (diff: 1 | -1) => {
    const features = community.features || {};
    const anySelected = Object.values(features).some(Boolean);

    if (!anySelected && diff === 1) {
      setDeferredStepChange(diff);
      setWarningOpen(true);
    } else {
      setStep(diff);
    }
  };

  return (
    <>
      <StepFlowStep
        disabled={community.features === undefined}
        setStep={wrappedSetStep}
        title={t('page.onboarding.communityFeaturesScreen.title')}
        subtitle={t('page.onboarding.communityFeaturesScreen.subtitle')}
        className="max-w-4xl">
        <FeaturesForm
          features={community.features || {}}
          onFeaturesChange={(features) => {
            const requiredSubscription = getMinimumSubscriptionForFeatures(features);
            setCommunity({
              ...community,
              connectedMembers: features.EASY_SHARING,
              features,
              subscriptionType: requiredSubscription,
            });
          }}
        />
      </StepFlowStep>

      <AreYouSureModal
        open={warningOpen}
        setOpen={setWarningOpen}
        title={t('page.onboarding.communityFeaturesScreen.modal.title')}
        description={t('page.onboarding.communityFeaturesScreen.modal.description')}
        confirmLabel={t('page.onboarding.communityFeaturesScreen.modal.confirm')}
        cancelLabel={t('page.onboarding.communityFeaturesScreen.modal.cancel')}
        onConfirm={() => {
          setWarningOpen(false);
          if (deferredStepChange) {
            setStep(deferredStepChange);
            setDeferredStepChange(null);
          }
        }}
        onCancel={() => {
          setWarningOpen(false);
          setDeferredStepChange(null);
        }}
      />
    </>
  );
}